import { useEffect, useState } from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function readReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/** True when the visitor asked the system for less motion. Follows live changes. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(readReducedMotion);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => setReduced(query.matches);
    onChange();
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);

  return reduced;
}

const easeOutExpo = (t: number) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));

/**
 * Counts from 0 to `target` once it is known. Returns `null` while the target
 * is unknown, so callers never show a misleading 0. With reduced motion, or
 * without requestAnimationFrame, the final value comes straight away.
 */
export function useCountUp(target: number | null, durationMs = 1600, delayMs = 0): number | null {
  const reduced = usePrefersReducedMotion();
  const [value, setValue] = useState<number | null>(() =>
    target === null ? null : reduced ? target : 0,
  );

  useEffect(() => {
    if (target === null) {
      setValue(null);
      return;
    }
    if (reduced || typeof window.requestAnimationFrame !== 'function') {
      setValue(target);
      return;
    }

    let frame = 0;
    let start: number | null = null;
    const tick = (now: number) => {
      if (start === null) start = now + delayMs;
      const progress = Math.max(0, Math.min(1, (now - start) / durationMs));
      setValue(Math.round(target * easeOutExpo(progress)));
      if (progress < 1) frame = window.requestAnimationFrame(tick);
    };
    setValue(0);
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [target, reduced, durationMs, delayMs]);

  return value;
}

/** 128450 -> "128 450" in French, "128,450" in English. */
export function formatFigure(value: number, lang: string | undefined): string {
  return value.toLocaleString(lang === 'en' ? 'en-US' : 'fr-FR');
}
